import { useParams, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SEOHead from "@/components/base/SEOHead";
import SchemaOrg from "@/components/base/SchemaOrg";
import Breadcrumb from "@/components/base/Breadcrumb";
import { findArticleBySlug, comicArticles, findRelatedArticles } from "@/mocks/articleData";

function ArticleContent({ content }: { content: string }) {
  return (
    <div
      className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed"
      dangerouslySetInnerHTML={{ __html: content }}
    />
  );
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [notFound, setNotFound] = useState(false);

  const article = slug ? findArticleBySlug(slug) : null;

  useEffect(() => {
    if (slug && !article) {
      setNotFound(true);
    } else {
      setNotFound(false);
    }
  }, [slug, article]);

  if (notFound) {
    return (
      <>
        <Navbar />
        <main className="pt-24 pb-20 text-center">
          <i className="ri-file-search-line w-16 h-16 flex items-center justify-center text-foreground-300 mx-auto mb-4 text-4xl" />
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-2">文章未找到</h1>
          <p className="text-foreground-500 mb-6">抱歉，您访问的文章不存在或已被移除。</p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors duration-200">
            <i className="ri-home-line w-4 h-4 flex items-center justify-center" />
            返回首页
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  if (!article) return null;

  const data = article.data;
  const isComic = article.type === "comic";
  const comicData = isComic ? (data as typeof comicArticles[0]) : null;
  const relatedArticles = comicData ? findRelatedArticles(comicData.relatedIds) : [];

  const title = data.title;
  const description = "excerpt" in data ? data.excerpt : data.description;
  const canonicalUrl = `https://www.51manhua.com/article/${encodeURIComponent(title)}`;

  const dateStr = "publishDate" in data ? data.publishDate : data.date;

  return (
    <>
      <SEOHead
        title={`${title} | 51漫画免费在线观看 - ${description}`}
        description={description}
        keywords={`${title}, 51漫画, 免费漫画, 在线观看, ${isComic ? data.category : ""}`}
        canonicalUrl={canonicalUrl}
      />

      <SchemaOrg
        type="article"
        data={{
          "@type": "Article",
          headline: title,
          description: description,
          url: canonicalUrl,
          datePublished: dateStr,
          dateModified: dateStr,
          author: {
            "@type": "Organization",
            name: "51漫画",
          },
          publisher: {
            "@type": "Organization",
            name: "51漫画",
            logo: {
              "@type": "ImageObject",
              url: "https://www.51manhua.com/logo.png",
            },
          },
          image: "image" in data ? data.image : data.cover,
        }}
      />

      <SchemaOrg
        type="breadcrumb"
        data={{
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "首页",
              item: "https://www.51manhua.com/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: title,
              item: canonicalUrl,
            },
          ],
        }}
      />

      <Navbar />

      <main className="pt-16 md:pt-18 bg-background-50">
        <Breadcrumb
          items={[
            { label: "首页", href: "/" },
            { label: "文章详情" },
          ]}
        />

        <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 py-8 md:py-12">
            <article className="flex-1 min-w-0">
              <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700 mb-4">
                {isComic ? data.category : (data as typeof import("@/mocks/articleData").newsArticles[0]).category}
              </span>

              <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-950 leading-tight text-balance">
                {title}
              </h1>

              <div className="flex items-center gap-4 mt-4 text-sm text-foreground-400">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line w-4 h-4 flex items-center justify-center" />
                  {dateStr}
                </span>
                {isComic && (
                  <>
                    <span className="flex items-center gap-1">
                      <i className="ri-user-line w-4 h-4 flex items-center justify-center" />
                      {data.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line w-4 h-4 flex items-center justify-center" />
                      {data.views}
                    </span>
                  </>
                )}
              </div>

              {isComic && (
                <div className="mt-6 rounded-2xl overflow-hidden aspect-[16/9] bg-background-100">
                  <img
                    src={data.cover}
                    alt={title}
                    className="w-full h-full object-cover object-top"
                    loading="eager"
                  />
                </div>
              )}

              {!isComic && "image" in data && (
                <div className="mt-6 rounded-2xl overflow-hidden aspect-[16/9] bg-background-100">
                  <img
                    src={data.image}
                    alt={title}
                    className="w-full h-full object-cover object-top"
                    loading="eager"
                  />
                </div>
              )}

              <div className="mt-8">
                <ArticleContent content={data.content} />
              </div>

              <div className="mt-10 pt-6 border-t border-background-200">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-foreground-500">标签：</span>
                  {isComic && data.tags.map((tag: string) => (
                    <Link
                      key={tag}
                      to={`/category/${tag === "热血" || tag === "玄幻" || tag === "武侠" ? "rexue" : tag === "恋爱" || tag === "耽美" ? "lianai" : tag === "悬疑" || tag === "异能" || tag === "都市" ? "xuanyi" : tag === "搞笑" || tag === "日常" ? "gaoxiao" : tag === "科幻" ? "kehuan" : "xianxia"}`}
                      className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs hover:bg-secondary-200 transition-colors duration-200"
                    >
                      {tag}
                    </Link>
                  ))}
                  {!isComic && (
                    <span className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs">
                      {(data as typeof import("@/mocks/articleData").newsArticles[0]).category}
                    </span>
                  )}
                </div>
              </div>
            </article>

            <aside className="w-full lg:w-80 flex-shrink-0">
              <div className="bg-accent-500 rounded-3xl p-6 text-background-50 sticky top-24">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-medium bg-background-50/20 px-3 py-1 rounded-full">
                    相关推荐
                  </span>
                  <div className="flex gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-background-50/60" />
                    <span className="w-1.5 h-1.5 rounded-full bg-background-50/60" />
                    <span className="w-1.5 h-1.5 rounded-full bg-background-50/60" />
                  </div>
                </div>

                {relatedArticles.length > 0 ? (
                  <div className="space-y-3">
                    {relatedArticles.map((ra) => (
                      <Link
                        key={ra?.id}
                        to={`/article/${ra?.title}`}
                        className="flex items-center gap-3 group"
                      >
                        <div className="w-14 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-background-50/20">
                          <img
                            src={ra?.cover}
                            alt={ra?.title || ""}
                            className="w-full h-full object-cover object-top"
                            loading="lazy"
                          />
                        </div>
                        <div>
                          <h4 className="font-heading text-sm font-semibold group-hover:text-accent-200 transition-colors duration-200 line-clamp-2">
                            {ra?.title}
                          </h4>
                          <p className="text-xs text-background-50/70 mt-0.5">{ra?.author}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {comicArticles.slice(0, 4).map((ra) => (
                      <Link
                        key={ra.id}
                        to={`/article/${ra.title}`}
                        className="flex items-center gap-3 group"
                      >
                        <div className="w-14 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-background-50/20">
                          <img
                            src={ra.cover}
                            alt={ra.title}
                            className="w-full h-full object-cover object-top"
                            loading="lazy"
                          />
                        </div>
                        <div>
                          <h4 className="font-heading text-sm font-semibold group-hover:text-accent-200 transition-colors duration-200 line-clamp-2">
                            {ra.title}
                          </h4>
                          <p className="text-xs text-background-50/70 mt-0.5">{ra.author}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}

                <Link
                  to="/"
                  className="mt-6 flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-background-50/20 text-sm font-medium hover:bg-background-50/30 transition-colors duration-200"
                >
                  浏览更多
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}